import { useState } from "react";
import { motion } from "framer-motion";
import { Brain, Filter, TrendingUp, BarChart3, DollarSign, Zap, ShieldCheck } from "lucide-react";
import { useFinancialInsights, InsightCard, InsightType } from "@/components/insights/AIInsightsPanel";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const TYPE_FILTERS: { value: InsightType | "all"; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { value: "all", label: "All Insights", icon: Brain },
  { value: "revenue", label: "Revenue", icon: TrendingUp },
  { value: "expense", label: "Expenses", icon: BarChart3 },
  { value: "cashflow", label: "Cash Flow", icon: DollarSign },
  { value: "profitability", label: "Profitability", icon: Zap },
  { value: "tax", label: "Tax", icon: ShieldCheck },
];

const InsightsPage = () => {
  const insights = useFinancialInsights();
  const [filter, setFilter] = useState<InsightType | "all">("all");

  const filtered = filter === "all" ? insights : insights.filter((i) => i.type === filter);

  const criticalCount = insights.filter((i) => i.severity === "critical").length;
  const warningCount = insights.filter((i) => i.severity === "warning").length;
  const positiveCount = insights.filter((i) => i.severity === "positive").length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground flex items-center gap-2">
            <Brain className="h-6 w-6 text-accent" />
            AI Financial Insights
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Smart analysis of your accounting data with actionable recommendations
          </p>
        </div>
        <div className="flex items-center gap-3">
          {criticalCount > 0 && (
            <span className="text-xs font-medium bg-destructive/10 text-destructive px-2 py-1 rounded-full">
              {criticalCount} Critical
            </span>
          )}
          {warningCount > 0 && (
            <span className="text-xs font-medium bg-warning/10 text-warning px-2 py-1 rounded-full">
              {warningCount} Warnings
            </span>
          )}
          <span className="text-xs font-medium bg-success/10 text-success px-2 py-1 rounded-full">
            {positiveCount} Positive
          </span>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {TYPE_FILTERS.slice(1).map((tf) => {
          const count = insights.filter((i) => i.type === tf.value).length;
          const isActive = filter === tf.value;
          return (
            <motion.button
              key={tf.value}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setFilter(isActive ? "all" : tf.value)}
              className={`p-3 rounded-lg border text-left transition-all ${
                isActive
                  ? "bg-accent/10 border-accent/30 ring-1 ring-accent/20"
                  : "bg-card border-border hover:border-accent/20"
              }`}
            >
              <tf.icon className={`h-4 w-4 mb-1.5 ${isActive ? "text-accent" : "text-muted-foreground"}`} />
              <div className="text-lg font-bold text-foreground">{count}</div>
              <div className="text-[11px] text-muted-foreground">{tf.label}</div>
            </motion.button>
          );
        })}
      </div>

      {/* Insights List */}
      <div className="space-y-3">
        {filtered.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Brain className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No insights available for this category</p>
          </div>
        ) : (
          filtered.map((insight, i) => (
            <InsightCard key={insight.id} insight={insight} index={i} />
          ))
        )}
      </div>
    </div>
  );
};

export default InsightsPage;
